import type { GaVueComponent } from "@/common/vueUtils";
import { GaAlert } from "@/components/general/GaAlert";
import { AiTool } from "@/components/miab/pageComponents/AiTool";
import { Edit } from "@/components/miab/pageComponents/Edit";
import { type DiscussEdit, MIAB_MAX_QUESTIONS } from "@/components/miab/MIABPage";
import { GaButton } from "@/components/general/GaButton";
import type { Store } from "@/common/storeUtils";
import type { MIABQuestionMetadata, MIABView } from "@newgenerated/shared/schema";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import { useConfirmationModal } from "@/components/general/GaModal";
import { saveCustomQuestion } from "@generated/api/mIABControllerApi";
import { addToast } from "@/components/general/GaToast";
import { GaSpinner } from "@/components/general/GaSpinner";
import type { TranslationFn } from "@utils/vue-migration/translationUtils";

function addQuestion(questions: Store<MIABQuestionMetadata[]>): void {
  const highestOrder = [...questions.get()].sort((a, b) => b.sortOrder - a.sortOrder)[0]?.sortOrder ?? 0;
  questions.set([...questions.get(), { title: "", question: "", sortOrder: highestOrder + 1, createdAt: null }]);
}

async function save(state: Store<DiscussEdit>, dataId: number, t: TranslationFn, questions: Store<MIABQuestionMetadata[]>, changeToView: () => void): Promise<void> {
  const unpackedState = state.unpackUnion();
  const { currentLanguage } = useGaContext();
  const popup = await useConfirmationModal()({
    message: t("miab:edit.form.confirm.message") ?? "",
    confirmLabel: t("miab:edit.form.confirm.ok"),
    cancelLabel: t("miab:edit.form.confirm.cancel"),
  });
  if (popup) {
    const result = await saveCustomQuestion({ questions: [...unpackedState.store.get().modifiedQuestions], dataId: dataId });
    if (result.kind !== "VALIDATION_ERROR") {
      if (result.kind === "UPDATED") {
        addToast({
          variant: "success",
          message: t("miab:edit.form.success.message") ?? "",
        });
        questions.set(unpackedState.store.sub("modifiedQuestions").get());
        changeToView();
      } else {
        window.location.href = window.location.origin + "/" + currentLanguage() + "/miab/" + result.miabId + "?hash=" + result.hash;
      }
    } else {
      unpackedState.store.sub("errors").set(result.validationErrorReasons);
    }
  }
}

export function EditForm(props: { state: Store<DiscussEdit>; t: TranslationFn; miab: MIABView; questionStore: Store<MIABQuestionMetadata[]>; changeToEdit: () => void }): GaVueComponent {
  const t = props.t;
  const aiToolState = props.state.sub("aiTool").unpackUnion();
  return (
    <>
      {props.state.get().errors.general.length > 0 ? (
        <div class="mb-4">
          <GaAlert variant={"danger"}>
            <ul class="mb-0">
              {props.state.get().errors.general.map((error) => (
                <li>{error}</li>
              ))}
            </ul>
          </GaAlert>
        </div>
      ) : null}
      {aiToolState.kind !== "CLOSED" ? <AiTool state={aiToolState.store} t={props.t} dataId={props.miab.content.data.summaryId} setQuestions={props.state.sub("modifiedQuestions").set} /> : null}
      {aiToolState.kind !== "CLOSED" && aiToolState.store.get().status === "LOADING" ? (
        <GaSpinner />
      ) : (
        <>
          {aiToolState.kind === "OPEN" ? <p class="lead">{t("miab:edit.questions.title")}</p> : null}
          <Edit questions={props.state.sub("modifiedQuestions")} errors={props.state.sub("errors")} t={t} />
          <div class="d-flex justify-content-end gap-2">
            {props.state.sub("modifiedQuestions").get().length < MIAB_MAX_QUESTIONS ? (
              <GaButton variant={"primary-alt"} onClick={() => addQuestion(props.state.sub("modifiedQuestions"))}>
                {t("miab:edit.form.addQuestion")}
              </GaButton>
            ) : null}
            {props.miab.userContext == null ? (
              <GaButton variant={"primary-alt"} onClick={() => props.state.sub("modifiedQuestions").set(props.miab.content.data.questions)}>
                {t("miab:edit.form.revert")}
              </GaButton>
            ) : null}
            <GaButton variant={"primary"} onClick={() => save(props.state, props.miab.content.data.summaryId, t, props.questionStore, props.changeToEdit)}>
              {t("miab:edit.form.save")}
            </GaButton>
          </div>
        </>
      )}
    </>
  );
}
